/**
 * GAEB DA XML 3.2 (X83) Parser
 * Deterministic XML parsing for Bill of Quantities (Leistungsverzeichnis) files.
 */
const GAEB_NS = 'http://www.gaeb.de/GAEB_DA_XML/DA83/3.2';

function getEl(parent, tagName) {
  return parent?.getElementsByTagNameNS(GAEB_NS, tagName)[0] || parent?.getElementsByTagName(tagName)[0] || null;
}

function getEls(parent, tagName) {
  const ns = parent?.getElementsByTagNameNS(GAEB_NS, tagName);
  if (ns && ns.length > 0) return Array.from(ns);
  return Array.from(parent?.getElementsByTagName(tagName) || []);
}

function txt(parent, tagName) {
  const el = getEl(parent, tagName);
  return el?.textContent?.trim() || '';
}

/**
 * Extract text from an XML element by walking its DOM tree.
 * - Each <p> becomes a paragraph (joined by \n)
 * - <br/> within a <p> is treated as a space (soft word-wrap from fixed-width layout)
 * - TextComplement children are skipped (extracted separately)
 * - Falls back to textContent if no <p> elements found
 */
function extractText(el, { skipComplements = false } = {}) {
  if (!el) return '';
  const pEls = getEls(el, 'p');
  if (pEls.length === 0) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }
  const paragraphs = pEls.map(p => {
    let text = '';
    for (const node of p.childNodes) {
      const tag = node.localName || node.tagName?.replace(/^.*:/, '');
      if (skipComplements && tag === 'TextComplement') continue;
      if (node.nodeType === 3) { // text node
        text += node.textContent;
      } else if (tag === 'br') {
        text += ' '; // soft wrap → space
      } else if (tag === 'span') {
        text += node.textContent || '';
      } else if (node.nodeType === 1) {
        text += node.textContent || '';
      }
    }
    return text.replace(/\s+/g, ' ').trim();
  });
  return paragraphs.filter(Boolean).join('\n');
}

function parseTextComplements(textEl) {
  if (!textEl) return [];
  return getEls(textEl, 'TextComplement').map(tc => ({
    markLabel: tc.getAttribute('MarkLbl') || '',
    kind: tc.getAttribute('Kind') || '',
    caption: extractText(getEl(tc, 'ComplCaption')),
    body: extractText(getEl(tc, 'ComplBody')),
  }));
}

function parseDescription(descEl) {
  if (!descEl) return { shortText: '', detailText: '', textComplements: [], stlNo: '' };
  const ct = getEl(descEl, 'CompleteText');
  const textEl = getEl(getEl(ct, 'DetailTxt'), 'Text');
  const outlTxtEl = getEl(ct, 'TextOutlTxt');
  return {
    stlNo: txt(descEl, 'StLNo'),
    detailText: extractText(textEl, { skipComplements: true }),
    textComplements: parseTextComplements(textEl),
    shortText: extractText(outlTxtEl),
  };
}

function parseRemark(el) {
  return { type: 'remark', id: el.getAttribute('ID') || '', ...parseDescription(getEl(el, 'Description')) };
}

function parseItem(el) {
  const ca = getEl(el, 'CtlgAssign');
  return {
    type: 'item', id: el.getAttribute('ID') || '', rnoPart: el.getAttribute('RNoPart') || '',
    qty: parseFloat(txt(el, 'Qty')) || 0, qu: txt(el, 'QU'),
    lumpSum: txt(el, 'LumpSumItem') === 'Yes',
    ctlgCode: txt(ca, 'CtlgCode'), ctlgId: txt(ca, 'CtlgID'),
    ...parseDescription(getEl(el, 'Description')),
  };
}

function parseItemlist(il) {
  if (!il) return [];
  return Array.from(il.children).map(c => {
    const tag = c.localName || c.tagName?.replace(/^.*:/, '');
    if (tag === 'Item') return parseItem(c);
    if (tag === 'Remark') return parseRemark(c);
    return null;
  }).filter(Boolean);
}

function parseBoQCtgy(el, parentPath = '') {
  const rno = el.getAttribute('RNoPart') || '';
  const label = extractText(getEl(el, 'LblTx'));
  const fp = parentPath ? `${parentPath}.${rno}` : rno;
  const children = [], items = [];
  const body = getEl(el, 'BoQBody');
  if (body) {
    for (const c of body.children) {
      const tag = c.localName || c.tagName?.replace(/^.*:/, '');
      if (tag === 'BoQCtgy') children.push(parseBoQCtgy(c, fp));
      else if (tag === 'Itemlist') items.push(...parseItemlist(c));
      else if (tag === 'Remark') items.push(parseRemark(c));
    }
  }
  return { rnoPart: rno, label, fullPath: fp, children, items };
}

export function parseGaebXml(xmlString) {
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  const pe = doc.querySelector('parsererror');
  if (pe) throw new Error('Invalid XML: ' + pe.textContent);
  const prj = getEl(doc, 'PrjInfo');
  const gi = getEl(doc, 'GAEBInfo');
  const ownAddr = getEl(getEl(doc, 'OWN'), 'Address');
  const ai = getEl(doc, 'AwardInfo');
  const bi = getEl(doc, 'BoQInfo');
  const project = { name: txt(prj, 'NamePrj'), label: txt(prj, 'LblPrj'), currency: txt(prj, 'Cur'), currencyLabel: txt(prj, 'CurLbl') };
  const meta = { version: txt(gi, 'Version'), date: txt(gi, 'Date'), time: txt(gi, 'Time'), progSystem: txt(gi, 'ProgSystem'), progName: txt(gi, 'ProgName') };
  const owner = { name1: txt(ownAddr, 'Name1'), name2: txt(ownAddr, 'Name2'), name3: txt(ownAddr, 'Name3'), street: txt(ownAddr, 'Street'), pcode: txt(ownAddr, 'PCode'), city: txt(ownAddr, 'City') };
  const award = { category: txt(ai, 'Cat'), currency: txt(ai, 'Cur') };
  const boqInfo = { name: txt(bi, 'Name'), label: txt(bi, 'LblBoQ'), date: txt(bi, 'Date') };
  const categories = [];
  const boqBody = getEl(doc, 'BoQBody');
  if (boqBody) {
    for (const c of boqBody.children) {
      const tag = c.localName || c.tagName?.replace(/^.*:/, '');
      if (tag === 'BoQCtgy') categories.push(parseBoQCtgy(c));
    }
  }
  return { project, meta, owner, award, boqInfo, categories };
}

export function flattenItems(categories, parentPath = '') {
  const r = [];
  for (const cat of categories) {
    const path = parentPath ? `${parentPath} > ${cat.label}` : cat.label;
    for (const item of cat.items) r.push({ ...item, sectionPath: path, sectionRno: cat.fullPath });
    r.push(...flattenItems(cat.children, path));
  }
  return r;
}

/**
 * Serialize parsed GAEB data into a concise text summary for LLM context.
 * Keeps it compact to fit token limits while preserving key information.
 */
export function serializeForChat(parsedData) {
  const { project, owner, boqInfo, award, categories } = parsedData;

  // Serialize BoQ hierarchy with items
  function serializeCategory(cat, depth = 0) {
    const indent = '  '.repeat(depth);
    let text = `${indent}[${cat.rnoPart}] ${cat.label}\n`;
    for (const item of cat.items) {
      if (item.type === 'remark') {
        text += `${indent}  HINWEIS: ${item.shortText || item.detailText?.substring(0, 200)}\n`;
      } else {
        const qtyStr = item.lumpSum ? 'Pauschale' : `${item.qty} ${item.qu}`;
        text += `${indent}  Pos. ${item.rnoPart}: ${item.shortText} | ${qtyStr}`;
        if (item.detailText) text += ` | ${item.detailText.substring(0, 300)}`;
        if (item.textComplements?.length) {
          const tcs = item.textComplements.map(tc => `${tc.caption}${tc.body}`).join('; ');
          text += ` | TC: ${tcs.substring(0, 200)}`;
        }
        text += '\n';
      }
    }
    for (const child of cat.children) {
      text += serializeCategory(child, depth + 1);
    }
    return text;
  }

  const boqSummary = categories.map(c => serializeCategory(c)).join('');

  return {
    projectLabel: project.label,
    projectName: project.name,
    ownerName: [owner.name1, owner.name2, owner.name3].filter(Boolean).join(', '),
    ownerAddress: `${owner.street}, ${owner.pcode} ${owner.city}`,
    boqLabel: boqInfo.label,
    date: boqInfo.date,
    currency: project.currency,
    awardCategory: award.category === 'OpenProc' ? 'Öffentliche Ausschreibung' : award.category,
    boqSummary,
  };
}

/**
 * Extract structured asphalt layer data from parsed categories.
 * Identifies actual layer construction items and extracts mix, binder, filler,
 * thickness, area, and notes deterministically from text patterns.
 */
export function getAsphaltLayerSummary(categories) {
  const allItems = flattenItems(categories).filter(i => i.type === 'item');
  const layers = [];

  // Only match items that actually construct a layer (must contain "herstellen")
  // or are tack coat items (Bitumenemulsion + Schichtenverbund/aufsprühen)
  const layerPatterns = [
    { regex: /Asphalttragschicht.*herstellen/i, layer: 'Tragschicht' },
    { regex: /Asphaltbinderschicht.*herstellen/i, layer: 'Binderschicht' },
    { regex: /Asphaltdeckschicht.*herstellen/i, layer: 'Deckschicht' },
    { regex: /Asphaltschutzschicht.*herstellen/i, layer: 'Schutzschicht' },
    { regex: /Bitumenemulsion.*Schichtenverbund|Schichtenverbund.*aufsp|Bitumenemulsion.*aufsp/i, layer: 'Schichtenverbund' },
    { regex: /Gussasphalt.*herstellen|Vorlegestreifen.*herstellen/i, layer: 'Schutzschicht' },
  ];

  // Exclude auxiliary items (milling, joints, cracks, cleaning, marking, etc.)
  const excludePatterns = /fr[äa]sen|Fuge.*herstellen|Riss.*behandeln|reinigen|entfernen|Erschwernis|Abstumpfung|Abstreuk|Anschluss.*Fuge|Oberfl[äa]che.*bearbeiten|Straßenscan|Vernetzung|Probeentnahme/i;

  // Match full mix designations including SP/SG suffixes
  const mixRegex = /\b(AC\s+\d+\s+[TBDS]\s+(?:S\s*(?:SG|SP)?|SP|SG)|MA\s+\d+\s+S)\b/i;
  const thicknessRegex = /Einbaudicke\s*=?\s*(\d+[,.]?\d*)\s*cm/i;
  const emulsionRegex = /Bindemittel\s*=\s*(C\d+[A-Z0-9-]+)/i;
  const emulsionQtyRegex = /Bindemittelmenge\s*=\s*(\d+\s*g\/m2)/i;
  const fillerRegex = /(?:Fremdf[üu]ller|Filler)\s*=\s*([^.]+)\./i;

  for (const item of allItems) {
    const fullText = `${item.shortText} ${item.detailText}`;
    const tcTexts = item.textComplements.map(tc => `${tc.caption} ${tc.body}`).join(' ');
    const combined = `${fullText} ${tcTexts}`;

    // Skip auxiliary/non-construction items
    if (excludePatterns.test(fullText)) continue;

    // Must match a layer construction pattern
    let layerType = null;
    for (const lp of layerPatterns) {
      if (lp.regex.test(fullText) || lp.regex.test(combined)) {
        layerType = lp.layer;
        break;
      }
    }
    if (!layerType) continue;

    // Extract mix designation
    const mixMatch = combined.match(mixRegex);
    let mix = mixMatch ? mixMatch[1].replace(/\s+/g, ' ').trim().toUpperCase() : '—';
    // For Schutzschicht also check for "MA 11 S" in text
    if (mix === '—' && /MA\s*11\s*S/i.test(combined)) mix = 'MA 11 S';

    // Extract thickness from inline text
    let thickness = '—';
    const thkMatch = fullText.match(thicknessRegex);
    if (thkMatch) {
      thickness = parseFloat(thkMatch[1].replace(',', '.')) + ' cm';
    }
    // Check TextComplements for Einbaudicke
    if (thickness === '—') {
      for (const tc of item.textComplements) {
        const tcBody = tc.body.replace(/^'|'$/g, '');
        if (/Einbau/i.test(tc.caption) || /Einbaudicke/i.test(tc.caption)) {
          const tcThk = tcBody.match(/(\d+[,.]?\d*)\s*cm/i);
          if (tcThk) { thickness = parseFloat(tcThk[1].replace(',', '.')) + ' cm'; break; }
        }
        // Also check for thickness in body even without caption (e.g. MarkLbl 61)
        if (!tc.caption && /\d+[,.]?\d*\s*cm/i.test(tcBody)) {
          const tcThk = tcBody.match(/(\d+[,.]?\d*)\s*cm/i);
          if (tcThk) { thickness = parseFloat(tcThk[1].replace(',', '.')) + ' cm'; break; }
        }
      }
    }

    // Extract binder
    let binder = '—';
    for (const tc of item.textComplements) {
      const tcBody = tc.body.replace(/^'|'$/g, '').replace(/\.$/, '');
      if (/Bindemittel/i.test(tc.caption) || (!tc.caption && /^res\.\[|^PmB/i.test(tcBody))) {
        // Clean up: remove res.[ ], trailing ], extra info after binder spec
        binder = tcBody
          .replace(/^res\.\[/, '').replace(/\]\s*[,.]?\s*$/, '').replace(/\]/, '')
          .replace(/\/\//g, ' / ')
          .replace(/,\s*Asphaltgranulat.*/i, '')  // remove trailing non-binder info
          .replace(/nach Unterlagen des AG\s*\.?\s*$/i, '')  // remove generic suffix
          .trim();
        break;
      }
    }
    if (binder === '—') {
      const emMatch = fullText.match(emulsionRegex);
      if (emMatch) binder = emMatch[1];
    }

    // Extract filler
    let filler = '—';
    const fillerMatch = fullText.match(fillerRegex);
    if (fillerMatch) filler = fillerMatch[1].trim();

    // Only include if at least one material spec was found
    // (Schichtenverbund is OK with just binder)
    const hasSpecs = mix !== '—' || binder !== '—' || filler !== '—' || thickness !== '—';
    if (!hasSpecs && layerType !== 'Schichtenverbund') continue;

    // Area
    const area = item.qu === 'm2'
      ? `${item.qty.toLocaleString('de-DE')} m²`
      : `${item.qty.toLocaleString('de-DE')} ${item.qu}`;

    // Build notes
    const notes = [];
    if (/temperaturabgesenkt/i.test(fullText)) notes.push('Temperaturabgesenkt');
    if (/hei[ßs]\s*(auf|an)\s*hei[ßs]/i.test(combined)) notes.push('Kompakt (heiß auf heiß)');
    if (/ohne.*Asphaltgranulat/i.test(combined)) notes.push('Ohne Asphaltgranulat');
    else if (/Asphaltgranulat/i.test(combined)) notes.push('Mit Asphaltgranulat');
    if (/untere Schicht/i.test(fullText)) notes.push('Untere Schicht');
    if (/obere Schicht/i.test(fullText)) notes.push('Obere Schicht');
    if (/Vorlegestreifen/i.test(combined)) notes.push('Vorlegestreifen');
    if (/Bauwerk|Brücke|Überbau/i.test(combined)) notes.push('Auf Bauwerk');
    if (layerType === 'Schichtenverbund') {
      const emQtyMatch = fullText.match(emulsionQtyRegex);
      if (emQtyMatch) notes.push(emQtyMatch[1]);
      if (/vor.*Deckschicht|vor.*A\.?deckschicht/i.test(fullText)) notes.push('Vor Deckschicht');
      else if (/vor.*2\.\s*Lage|vor.*2\.\s*Lage.*ATS/i.test(fullText)) notes.push('Vor 2. Lage ATS');
      else if (/vor.*Binderschicht/i.test(fullText)) notes.push('Vor Binderschicht');
    }

    layers.push({
      layerType, mix, binder, filler, thickness, area,
      notes: notes.join(', ') || '—',
      rnoPart: `${item.sectionRno}.${item.rnoPart}`,
      shortText: item.shortText,
      sectionPath: item.sectionPath,
    });
  }

  return layers;
}
